import { useState, useMemo, useEffect } from "react";

/**
 * Custom hook to manage dropdown filters and filtered table data.
 * Optimized to reduce re-computation and improve performance with large datasets.
 * Persists filter selections in sessionStorage for the current browser session.
 * @param {Array} dropdownFields - List of fields to create dropdown filters for.
 * @param {Array} dropdownTable - Table data to filter based on dropdown selections.
 * @param {string} storageKey - Unique key for storing filter state in sessionStorage (optional).
 * @returns {Object} - Contains dropdown options, selected values, filtered table data, and a handler for dropdown changes.
 */
export function useDropdownFilters(dropdownFields, dropdownTable, storageKey = 'dropdownFilters') {
    // Initialize selected values for each dropdown field with "All" (indicating no filter applied).
    const getInitialSelectedValues = () => {
        const defaults = dropdownFields.reduce((acc, field) => {
            acc[field] = "All";
            return acc;
        }, {});

        // Try to load saved filter state from sessionStorage
        if (storageKey) {
            try {
                const saved = sessionStorage.getItem(storageKey);
                if (saved) {
                    const parsed = JSON.parse(saved);
                    // Only restore values for fields that exist in current component's dropdownFields
                    dropdownFields.forEach(field => {
                        if (parsed[field] !== undefined) {
                            defaults[field] = parsed[field];
                        }
                    });
                    return defaults;
                }
            } catch (error) {
                console.warn('Failed to load saved dropdown filters:', error);
            }
        }

        return defaults;
    };

    // State to store the currently selected values for each dropdown field.
    const [selectedValues, setSelectedValues] = useState(getInitialSelectedValues);

    // Save selected values to sessionStorage whenever they change
    useEffect(() => {
        if (storageKey) {
            try {
                sessionStorage.setItem(storageKey, JSON.stringify(selectedValues));
            } catch (error) {
                console.warn('Failed to save dropdown filters:', error);
            }
        }
    }, [selectedValues, storageKey]);

    // First, calculate ALL available options without any filtering to validate against
    const allAvailableOptions = useMemo(() => {
        if (dropdownTable.length === 0) {
            return dropdownFields.reduce((acc, field) => ({ ...acc, [field]: [] }), {});
        }

        const fieldSets = dropdownFields.reduce((acc, field) => {
            acc[field] = new Set();
            return acc;
        }, {});

        // Get all unique values for each field without any filtering
        dropdownTable.forEach(item => {
            dropdownFields.forEach(field => {
                if (item[field] != null) {
                    fieldSets[field].add(item[field]);
                }
            });
        });

        return dropdownFields.reduce((options, field) => {
            options[field] = Array.from(fieldSets[field]).sort();
            return options;
        }, {});
    }, [dropdownTable, dropdownFields]);

    // Validate selected values only when dropdown data changes (not on every selectedValues change)
    useEffect(() => {
        if (dropdownTable.length === 0) {
            return;
        }

        let needsUpdate = false;
        const validated = { ...selectedValues };

        dropdownFields.forEach(field => {
            const currentValue = selectedValues[field];
            const availableOptions = allAvailableOptions[field] || [];
            
            // Reset to "All" if current value doesn't exist in the actual data
            if (currentValue !== "All" && 
                availableOptions.length > 0 && 
                !availableOptions.includes(currentValue)) {
                validated[field] = "All";
                needsUpdate = true;
            }
        });

        if (needsUpdate) {
            setSelectedValues(validated);
        }
        // Only run when dropdown data or available options change, NOT when selectedValues change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownTable, allAvailableOptions]);

    /**
     * Memoized computation of dropdown options based on current table data and selected values.
     * Uses Sets for O(1) lookups and single-pass filtering for better performance.
     * Ensures that dropdown options are filtered and unique for each field (cascading filters).
     */
    const dropdownOptions = useMemo(() => {
        if (dropdownTable.length === 0) {
            return dropdownFields.reduce((acc, field) => ({ ...acc, [field]: [] }), {});
        }

        // Pre-create Sets for each field for O(1) insertion and automatic uniqueness
        const fieldSets = dropdownFields.reduce((acc, field) => {
            acc[field] = new Set();
            return acc;
        }, {});

        // Process each field separately to handle cascading filters correctly
        dropdownFields.forEach(field => {
            // Filter the table data based on selected values of OTHER fields only
            const filteredData = dropdownTable.filter(item =>
                dropdownFields.every(otherField =>
                    otherField === field || 
                    selectedValues[otherField] === "All" || 
                    item[otherField] === selectedValues[otherField]
                )
            );

            // Extract unique values for the current field using Set
            filteredData.forEach(item => {
                if (item[field] != null) {
                    fieldSets[field].add(item[field]);
                }
            });
        });

        // Convert Sets to sorted arrays
        return dropdownFields.reduce((options, field) => {
            options[field] = Array.from(fieldSets[field]).sort();
            return options;
        }, {});
    }, [dropdownTable, selectedValues, dropdownFields]);

    /**
     * Handler to update the selected value for a specific dropdown field.
     * @param {string} field - The field name of the dropdown.
     * @param {string} value - The selected value for the dropdown.
     */
    const handleDropdownChange = (field, value) => {
        setSelectedValues(prev => ({ ...prev, [field]: value }));
    };

    /**
     * Reset all dropdown filters to "All"
     */
    const resetAllFilters = () => {
        const resetValues = dropdownFields.reduce((acc, field) => {
            acc[field] = "All";
            return acc;
        }, {});
        setSelectedValues(resetValues);
    };

    // Return the dropdown options, selected values, and the change handler.
    return { dropdownOptions, selectedValues, handleDropdownChange, resetAllFilters };
}